/**
 * MindForge brand logo — the official lockups, inline.
 *
 * Artwork is generated from the brand source of truth in the repo-root
 * `brand/` directory (`*-color.svg` for each lockup); the two brand fills are
 * lifted out into `primary`/`accent` so a single set of paths serves every
 * colorway. Regenerate by re-lifting the fills if `brand/` ever changes —
 * `brand/README.md` has the palette.
 *
 * Inlined rather than referenced as a file so the component works in any
 * consuming app with no per-app public asset to keep in sync, stays crisp at
 * any size, and can pick up `currentColor` where a variant asks for it.
 */
import type { SVGProps } from "react";

import { cn } from "@/lib/utils";

/**
 * The official brand palette — Mindforge Logo Guidelines v1.0, p.7.
 *
 * Precision Blue carries the wordmark and the mark; Synapse Blue is the accent
 * that paints the Mind's Eye and the `o` in `forge`, and the guidelines require
 * it there "whenever possible". The remaining three are brand colours the logo
 * itself doesn't use — they're exported so app code has one place to get them
 * (and as CSS tokens from `styles.css`, which is what UI code should prefer).
 */
export const PRECISION_BLUE = "#0A3E88";
export const SYNAPSE_BLUE = "#18A9D8";
export const NEURAL_MIST = "#88D3EB";
export const CORTEX_NAVY = "#19376F";
export const GREY_MATTER = "#E9E9E9";

/**
 * `color` on light surfaces and `reversed` on brand navy / dark surfaces are
 * the two official colourways. `adaptive` is the one to reach for inside the
 * apps: it paints the wordmark brand navy on light and white on dark, keeping
 * the cyan accent, so one call is correct in either theme. `white`/`black` are for
 * single-colour reproduction, `mono` drops the accent too.
 */
export type LogoVariant = "color" | "reversed" | "adaptive" | "white" | "black" | "mono";

/** Which lockup to draw. Lettermark is the mark alone — use it for tight spots. */
export type LogoLockup = "horizontal" | "stacked" | "lettermark";

const PALETTE: Record<LogoVariant, { primary: string; accent: string }> = {
  color: { primary: PRECISION_BLUE, accent: SYNAPSE_BLUE },
  reversed: { primary: "#FFFFFF", accent: SYNAPSE_BLUE },
  adaptive: { primary: "currentColor", accent: SYNAPSE_BLUE },
  white: { primary: "#FFFFFF", accent: "#FFFFFF" },
  black: { primary: "#000000", accent: "#000000" },
  mono: { primary: "currentColor", accent: "currentColor" },
};

const HORIZONTAL = {
  viewBox: "0 0 5000 999",
  paths: (primary: string, accent: string) => (
    <>
      <path
        d="M4826.55 832.698C4720.31 832.698 4645.87 755.366 4645.87 644.79C4645.87 532.768 4718.86 455.437 4823.66 455.437C4930.62 455.437 4998.55 526.986 4998.55 638.286V665.026L4729.7 665.749C4736.21 728.626 4769.45 760.425 4827.99 760.425C4876.41 760.425 4908.21 741.635 4918.33 707.667H5000C4984.82 785.721 4919.78 832.698 4826.55 832.698ZM4824.38 527.709C4772.34 527.709 4740.54 555.895 4731.87 609.377H4911.1C4911.1 560.232 4877.14 527.709 4824.38 527.709Z"
        fill={primary}
      />
      <path
        d="M4205.88 636.858C4205.88 532.786 4273.81 454.732 4375.72 454.732C4429.2 454.732 4471.12 477.136 4492.08 515.441L4497.13 466.295H4578.8V805.252C4578.8 924.501 4507.25 998.941 4391.62 998.941C4288.99 998.941 4218.89 940.401 4208.05 845.002H4296.22C4302 891.256 4337.41 918.719 4391.62 918.719C4452.33 918.719 4491.35 880.415 4491.35 821.152V761.889C4468.95 795.134 4424.86 816.093 4373.55 816.093C4272.37 816.093 4205.88 740.207 4205.88 636.858ZM4294.77 634.69C4294.77 694.675 4333.08 739.484 4390.89 739.484C4451.6 739.484 4489.18 696.844 4489.18 634.69C4489.18 573.981 4452.33 532.063 4390.89 532.063C4332.35 532.063 4294.77 576.149 4294.77 634.69Z"
        fill={primary}
      />
      <path
        d="M4175.1 464.827V546.494H4142.57C4078.97 546.494 4039.22 580.462 4039.22 649.843V823.296H3951.05V466.995H4034.17L4039.22 519.031C4054.4 483.617 4088.37 459.768 4136.07 459.768C4148.36 459.768 4160.64 461.213 4175.1 464.827Z"
        fill={primary}
      />
      <path
        d="M3331.94 466.299V422.936C3331.94 326.091 3383.25 285.619 3460.58 285.619C3474.32 285.619 3490.94 286.341 3506.12 289.232V364.395H3475.04C3430.23 364.395 3419.39 387.522 3419.39 422.936V466.299H3504.67V540.017H3419.39V823.323H3331.94V540.017V466.299Z"
        fill={primary}
      />
      <path
        d="M3059.49 831.966C2957.58 831.966 2897.6 753.912 2897.6 649.117C2897.6 543.6 2957.58 464.101 3061.65 464.101C3119.47 464.101 3165.73 489.396 3190.3 539.986V296.429H3240.17V823.293H3196.8L3191.74 751.744C3167.17 805.948 3118.03 831.966 3059.49 831.966ZM3068.16 786.434C3144.77 786.434 3189.58 728.617 3189.58 647.672C3189.58 567.45 3144.77 508.909 3068.16 508.909C2992.27 508.909 2948.19 567.45 2948.19 647.672C2948.19 728.617 2992.27 786.434 3068.16 786.434Z"
        fill={primary}
      />
      <path
        d="M2565.96 823.324H2516.09V473.527H2559.46L2566.68 534.236C2589.81 489.427 2636.06 464.132 2686.65 464.132C2782.78 464.132 2822.53 521.227 2822.53 609.399V823.324H2772.66V620.24C2772.66 538.572 2735.8 509.663 2677.98 509.663C2606.43 509.663 2565.96 561.699 2565.96 640.476V823.324Z"
        fill={primary}
      />
      <path
        d="M2386.54 377.381C2366.31 377.381 2349.69 360.759 2349.69 340.522C2349.69 320.286 2366.31 302.941 2386.54 302.941C2406.78 302.941 2424.13 320.286 2424.13 340.522C2424.13 360.759 2406.78 377.381 2386.54 377.381ZM2361.97 823.3V473.503H2411.84V823.3H2361.97Z"
        fill={primary}
      />
      <path
        d="M1839.54 823.305H1788.23V305.837H1839.54L2028.17 674.12L2216.8 305.837H2268.84V823.305H2217.53V577.58C2217.53 487.24 2218.97 447.49 2221.86 412.077L2053.47 735.551H2003.6L1835.21 414.245C1838.1 433.759 1839.54 490.854 1839.54 569.63V823.305Z"
        fill={primary}
      />
      <path
        d="M3890.67 647.383C3890.67 751.659 3806.14 836.191 3701.86 836.191C3597.59 836.191 3513.05 751.659 3513.05 647.383C3513.05 543.107 3597.59 458.575 3701.86 458.575C3806.14 458.575 3890.67 543.107 3890.67 647.383ZM3596.99 647.383C3596.99 705.304 3643.94 752.258 3701.86 752.258C3759.78 752.258 3806.74 705.304 3806.74 647.383C3806.74 589.462 3759.78 542.508 3701.86 542.508C3643.94 542.508 3596.99 589.462 3596.99 647.383Z"
        fill={accent}
      />
      <path
        d="M1043.14 298.036C1125.44 298.036 1192.15 364.754 1192.15 447.054C1192.15 485.736 1196.67 527.628 1224.02 554.98L1233.25 564.207C1260.6 591.559 1302.49 596.074 1341.17 596.074C1423.47 596.074 1490.19 662.792 1490.19 745.092C1490.19 827.393 1423.47 894.111 1341.17 894.111C1258.87 894.111 1192.15 827.393 1192.15 745.092C1192.15 706.402 1187.64 664.499 1160.28 637.141L1151.08 627.943C1123.73 600.585 1081.82 596.073 1043.14 596.073C1004.45 596.073 962.561 600.587 935.209 627.939L925.983 637.165C898.631 664.517 894.117 706.41 894.117 745.092C894.117 827.393 827.399 894.111 745.099 894.111C662.798 894.111 596.08 827.393 596.08 745.092C596.08 706.402 591.567 664.499 564.208 637.141L555.011 627.944C527.652 600.585 485.747 596.073 447.056 596.073C408.375 596.073 366.483 600.587 339.132 627.939L329.902 637.168C302.551 664.52 298.037 706.411 298.037 745.092C298.037 827.393 231.319 894.111 149.019 894.111C66.718 894.111 -3.59747e-06 827.393 0 745.092C0.000161488 662.792 66.7181 596.074 149.019 596.074C187.708 596.074 229.611 591.561 256.968 564.203L266.166 555.006C293.524 527.648 298.037 485.744 298.037 447.054C298.037 364.754 364.755 298.036 447.056 298.036C529.356 298.036 596.074 364.754 596.074 447.054C596.074 485.733 600.589 527.623 627.939 554.973L637.173 564.207C664.525 591.559 706.417 596.074 745.099 596.074C783.787 596.074 825.688 591.561 853.045 564.204L862.246 555.002C889.604 527.645 894.117 485.743 894.117 447.054C894.117 364.754 960.835 298.036 1043.14 298.036Z"
        fill={primary}
      />
      <circle cx="745.101" cy="148.387" r="148.387" fill={accent} />
    </>
  ),
};

const STACKED = {
  viewBox: "0 0 3176 1884",
  paths: (primary: string, accent: string) => (
    <>
      <path
        d="M3003.78 1719.46C2898.74 1719.46 2825.15 1643 2825.15 1533.68C2825.15 1422.94 2897.32 1346.48 3000.92 1346.48C3106.67 1346.48 3173.83 1417.22 3173.83 1527.25V1553.69L2908.03 1554.4C2914.46 1616.57 2947.33 1648.01 3005.21 1648.01C3053.08 1648.01 3084.52 1629.43 3094.52 1595.85H3175.26C3160.25 1673.01 3095.95 1719.46 3003.78 1719.46ZM3001.63 1417.93C2950.19 1417.93 2918.75 1445.8 2910.18 1498.67H3087.37C3087.37 1450.09 3053.79 1417.93 3001.63 1417.93Z"
        fill={primary}
      />
      <path
        d="M2390.17 1525.84C2390.17 1422.95 2457.33 1345.79 2558.07 1345.79C2610.95 1345.79 2652.39 1367.94 2673.11 1405.81L2678.11 1357.22H2758.85V1692.32C2758.85 1810.22 2688.11 1883.81 2573.79 1883.81C2472.33 1883.81 2403.03 1825.93 2392.31 1731.62H2479.48C2485.19 1777.35 2520.21 1804.5 2573.79 1804.5C2633.81 1804.5 2672.39 1766.63 2672.39 1708.04V1649.45C2650.24 1682.32 2606.66 1703.04 2555.93 1703.04C2455.9 1703.04 2390.17 1628.02 2390.17 1525.84ZM2478.05 1523.7C2478.05 1583 2515.92 1627.3 2573.08 1627.3C2633.1 1627.3 2670.25 1585.15 2670.25 1523.7C2670.25 1463.68 2633.81 1422.24 2573.08 1422.24C2515.2 1422.24 2478.05 1465.82 2478.05 1523.7Z"
        fill={primary}
      />
      <path
        d="M2359.73 1355.77V1436.51H2327.58C2264.7 1436.51 2225.41 1470.09 2225.41 1538.68V1710.16H2138.24V1357.91H2220.41L2225.41 1409.35C2240.41 1374.34 2273.99 1350.76 2321.15 1350.76C2333.3 1350.76 2345.44 1352.19 2359.73 1355.77Z"
        fill={primary}
      />
      <path
        d="M1526.16 1357.22V1314.35C1526.16 1218.61 1576.89 1178.6 1653.34 1178.6C1666.92 1178.6 1683.35 1179.31 1698.36 1182.17V1256.48H1667.63C1623.33 1256.48 1612.62 1279.34 1612.62 1314.35V1357.22H1696.93V1430.1H1612.62V1710.19H1526.16V1430.1V1357.22Z"
        fill={primary}
      />
      <path
        d="M1256.81 1718.73C1156.06 1718.73 1096.76 1641.57 1096.76 1537.96C1096.76 1433.64 1156.06 1355.05 1258.95 1355.05C1316.11 1355.05 1361.84 1380.06 1386.13 1430.07V1189.28H1435.43V1710.16H1392.56L1387.56 1639.42C1363.27 1693.01 1314.68 1718.73 1256.81 1718.73ZM1265.38 1673.72C1341.12 1673.72 1385.42 1616.56 1385.42 1536.53C1385.42 1457.22 1341.12 1399.35 1265.38 1399.35C1190.36 1399.35 1146.77 1457.22 1146.77 1536.53C1146.77 1616.56 1190.36 1673.72 1265.38 1673.72Z"
        fill={primary}
      />
      <path
        d="M768.889 1710.19H719.588V1364.37H762.458L769.603 1424.39C792.467 1380.09 838.196 1355.08 888.211 1355.08C983.24 1355.08 1022.54 1411.53 1022.54 1498.69V1710.19H973.237V1509.41C973.237 1428.67 936.798 1400.09 879.637 1400.09C808.901 1400.09 768.889 1451.54 768.889 1529.42V1710.19Z"
        fill={primary}
      />
      <path
        d="M591.514 1269.31C571.508 1269.31 555.074 1252.88 555.074 1232.87C555.074 1212.87 571.508 1195.72 591.514 1195.72C611.52 1195.72 628.668 1212.87 628.668 1232.87C628.668 1252.88 611.52 1269.31 591.514 1269.31ZM567.221 1710.16V1364.34H616.522V1710.16H567.221Z"
        fill={primary}
      />
      <path
        d="M50.7299 1710.17H0V1198.58H50.7299L237.216 1562.68L423.702 1198.58H475.146V1710.17H424.416V1467.24C424.416 1377.92 425.845 1338.63 428.703 1303.62L262.223 1623.41H212.923L46.4428 1305.76C49.3009 1325.05 50.7299 1381.5 50.7299 1459.38V1710.17Z"
        fill={primary}
      />
      <path
        d="M2078.54 1536.25C2078.54 1639.34 1994.97 1722.91 1891.88 1722.91C1788.79 1722.91 1705.22 1639.34 1705.22 1536.25C1705.22 1433.16 1788.79 1349.59 1891.88 1349.59C1994.97 1349.59 2078.54 1433.16 2078.54 1536.25ZM1788.2 1536.25C1788.2 1593.51 1834.62 1639.93 1891.88 1639.93C1949.14 1639.93 1995.56 1593.51 1995.56 1536.25C1995.56 1478.99 1949.14 1432.56 1891.88 1432.56C1834.62 1432.56 1788.2 1478.99 1788.2 1536.25Z"
        fill={accent}
      />
      <path
        d="M1881.46 294.647C1962.82 294.647 2028.78 360.607 2028.78 441.972C2028.78 480.214 2033.25 521.63 2060.29 548.671L2069.41 557.793C2096.45 584.834 2137.87 589.298 2176.11 589.298C2257.47 589.298 2323.43 655.257 2323.43 736.622C2323.43 817.987 2257.47 883.946 2176.11 883.946C2094.74 883.946 2028.79 817.987 2028.79 736.622C2028.79 698.373 2024.32 656.947 1997.28 629.9L1988.18 620.804C1961.13 593.758 1919.71 589.296 1881.46 589.296C1843.22 589.296 1801.8 593.76 1774.76 620.801L1765.64 629.921C1738.6 656.963 1734.14 698.38 1734.14 736.622C1734.14 817.987 1668.18 883.946 1586.81 883.946C1505.45 883.946 1439.49 817.987 1439.49 736.622C1439.49 698.372 1435.03 656.946 1407.98 629.899L1398.88 620.804C1371.84 593.758 1330.41 589.296 1292.16 589.296C1253.92 589.296 1212.5 593.759 1185.46 620.8L1176.34 629.921C1149.3 656.963 1144.84 698.379 1144.84 736.622C1144.84 817.987 1078.88 883.946 997.512 883.946C916.147 883.946 850.187 817.987 850.188 736.622C850.188 655.257 916.147 589.298 997.512 589.298C1035.76 589.298 1077.18 584.835 1104.23 557.79L1113.33 548.693C1140.37 521.647 1144.84 480.221 1144.84 441.972C1144.84 360.607 1210.8 294.648 1292.16 294.647C1373.53 294.647 1439.48 360.607 1439.48 441.972C1439.48 480.212 1443.95 521.627 1470.99 548.668L1480.11 557.794C1507.15 584.834 1548.57 589.298 1586.81 589.298C1625.06 589.298 1666.48 584.836 1693.53 557.79L1702.63 548.693C1729.67 521.647 1734.14 480.221 1734.14 441.972C1734.14 360.607 1800.1 294.648 1881.46 294.647Z"
        fill={primary}
      />
      <circle cx="1586.81" cy="146.7" r="146.7" fill={accent} />
    </>
  ),
};

const LETTERMARK = {
  viewBox: "0 0 1474 884",
  paths: (primary: string, accent: string) => (
    <>
      <path
        d="M1031.27 294.647C1112.64 294.647 1178.6 360.607 1178.6 441.972C1178.6 480.214 1183.06 521.63 1210.1 548.671L1219.22 557.793C1246.26 584.834 1287.68 589.298 1325.92 589.298C1407.29 589.298 1473.25 655.257 1473.25 736.622C1473.25 817.987 1407.29 883.946 1325.92 883.946C1244.56 883.946 1178.6 817.987 1178.6 736.622C1178.6 698.373 1174.14 656.947 1147.09 629.9L1137.99 620.804C1110.95 593.758 1069.52 589.296 1031.27 589.296C993.031 589.296 951.614 593.76 924.573 620.801L915.453 629.921C888.411 656.963 883.948 698.38 883.948 736.622C883.948 817.987 817.989 883.946 736.624 883.946C655.259 883.946 589.3 817.987 589.3 736.622C589.3 698.372 584.838 656.946 557.791 629.899L548.696 620.804C521.649 593.758 480.223 589.296 441.973 589.296C403.731 589.296 362.314 593.759 335.273 620.8L326.152 629.921C299.111 656.963 294.648 698.379 294.648 736.622C294.648 817.987 228.689 883.946 147.324 883.946C65.9592 883.946 -3.55658e-06 817.987 0 736.622C5.43221e-05 655.257 65.9593 589.298 147.324 589.298C185.573 589.298 226.997 584.835 254.043 557.79L263.139 548.693C290.186 521.647 294.648 480.221 294.648 441.972C294.648 360.607 360.608 294.648 441.973 294.647C523.338 294.647 589.297 360.607 589.297 441.972C589.297 480.212 593.76 521.627 620.8 548.668L629.927 557.794C656.967 584.834 698.383 589.298 736.624 589.298C774.872 589.298 816.297 584.836 843.342 557.79L852.439 548.693C879.486 521.647 883.948 480.221 883.948 441.972C883.948 360.607 949.908 294.648 1031.27 294.647Z"
        fill={primary}
      />
      <circle cx="736.625" cy="146.7" r="146.7" fill={accent} />
    </>
  ),
};

const LOCKUPS: Record<LogoLockup, typeof HORIZONTAL> = {
  horizontal: HORIZONTAL,
  stacked: STACKED,
  lettermark: LETTERMARK,
};

export interface LogoProps extends Omit<SVGProps<SVGSVGElement>, "viewBox" | "fill"> {
  lockup?: LogoLockup;
  variant?: LogoVariant;
  /**
   * Accessible name. Pass `null` for a purely decorative logo (sat next to the
   * brand name in text, say) so screen readers skip it.
   */
  title?: string | null;
}

/**
 * Size it by constraining one axis and letting the other follow, e.g.
 * `className="h-8 w-auto"` — the intrinsic aspect ratio comes from the
 * lockup's viewBox.
 */
export function Logo({
  lockup = "horizontal",
  variant = "color",
  title = "MindForge",
  className,
  style,
  ...props
}: LogoProps) {
  const { viewBox, paths } = LOCKUPS[lockup];
  const { primary, accent } = PALETTE[variant];
  const [, , boxWidth, boxHeight] = viewBox.split(" ");

  return (
    <svg
      viewBox={viewBox}
      fill="none"
      // An inline <svg> carries no intrinsic width, so `h-6 w-auto` alone leaves
      // the width to the layout — in a flex column it stretches and the artwork
      // silently centres itself inside the excess. Publishing the lockup's own
      // ratio makes `w-auto` resolve to the real width and the mark align.
      style={{ aspectRatio: `${boxWidth} / ${boxHeight}`, ...style }}
      xmlns="http://www.w3.org/2000/svg"
      role={title === null ? "presentation" : "img"}
      aria-hidden={title === null ? true : undefined}
      aria-label={title ?? undefined}
      // `adaptive` draws the wordmark in currentColor, so the theme-flipped
      // brand colours are supplied here as text colour. Listed first so a
      // caller's own text-* class still wins.
      className={cn(variant === "adaptive" && "text-[#0A3E88] dark:text-white", className)}
      {...props}
    >
      {title !== null && <title>{title}</title>}
      {paths(primary, accent)}
    </svg>
  );
}

/** The mark on its own — shorthand for `<Logo lockup="lettermark" />`. */
export function Lettermark(props: Omit<LogoProps, "lockup">) {
  return <Logo lockup="lettermark" {...props} />;
}
